<template>
  <div>
    <h1 class="mb-8 font-bold text-3xl">
      <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('customer.orders')">My Orders</inertia-link>
      <span class="text-indigo-400 font-medium">/</span> Create
    </h1>

    <portal to="search">
      <div class="w-full h-full flex flex-row items-center">
        <div class="relative w-full text-gray-400 focus-within:text-gray-600">
          <div class="absolute inset-y-0 left-0 flex items-center pointer-events-none">
            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" />
            </svg>
          </div>
          <input v-model="searchText" class="block w-full h-full pl-8 pr-3 py-2 rounded-md text-gray-900 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 text-base" placeholder="Search" type="search">
        </div>
      </div>
    </portal>

    <div class="flex flex-col md:flex-row sm:-px-4 lg:-mx-4">
      <div class="md:block w-full order-2 md:order-1 md:w-6/12 lg:w-8/12 px-1" :class="{'hidden': mobile.showOrderForm}">
        <product-catalog v-if="selectedAccount"
                         :selected-items="form.items"
                         :priceGroup="selectedPriceGroup"
                         :searchText="searchText"
                         :location="shopping_location"
                         :key="selectedAccount.id"
                         :enable-product-unit-selection="enableProductUnitSelection"
                         @locationChanged="clearItems"
        />
      </div>

      <div class="w-full order-1 md:order-2 md:w-6/12 lg:w-4/12 px-1 lg:px-4 sticky bg-gray-200 md:relative z-10 md:top-0 sticky-top">
        <div class="md:hidden flex items-center justify-between pt-2 mb-2">
          <div>{{ form.items.length }} item{{ form.items.length > 1 ? 's' : '' }} added to order.</div>

          <button class="btn-primary text-xs px-2.5 py-1.5" @click="mobile.showOrderForm = !mobile.showOrderForm">{{ mobile.showOrderForm ? 'Hide' : 'Show' }}</button>
        </div>

        <form class="md:block pb-8 md:pb-0" :class="{'hidden': !mobile.showOrderForm}" @submit.prevent="submit">

          <div class="flex flex-col md:flex-row md:items-center w-full mt-4">
            <label class="block text-sm font-medium leading-5 text-gray-700 md:mr-3 flex-grow">
              Shopping Location
            </label>
            <div class="flex rounded-md shadow-sm">
              <select v-model="shopping_location" class="block form-select w-full transition duration-150 ease-in-out sm:text-sm">
                <option v-for="(shopLocation, slkey) in addressConfig.internal_shopping_locations" :value="slkey">{{ shopLocation }}</option>
              </select>
            </div>
          </div>

          <div class="block mb-3">
            <div class="block">
              <label class="block text-sm font-medium leading-5 text-gray-700">
                Customer Shop / Account
              </label>
              <div class="mt-1 flex rounded-md shadow-sm">
                <v-select id="shop" v-model="selectedAccount" class="tailwind-v-select w-full" :filterable="false" :options="customerAccounts" :get-option-label="account => `${account.name} - (${account.tin_number})`" @search="searchCustomerAccounts">
                  <!--                    <option v-for="account in accounts" :value="account.id">{{ `${account.name} (TIN: ${account.tin_number})` }}</option>-->
                </v-select>
              </div>
            </div>
          </div>

          <div class="block mb-3">
            <div class="block">
              <label class="block text-sm font-medium leading-5 text-gray-700">
                Price Group
              </label>
              <div class="mt-1 flex rounded-md shadow-sm">
                <select v-model="selectedPriceGroup" aria-label="Country"
                        class="form-select relative block w-full rounded-t-md">
                  <option disabled></option>
                  <option v-for="(price_group, pg_index) in price_groups" :value="pg_index">{{ price_group }}</option>
                </select>
              </div>
            </div>
          </div>


          <div class="block mb-3">
            <div class="block">
              <label class="block text-sm font-medium leading-5 text-gray-700">
                Customer Reference
              </label>
              <div class="mt-1 flex rounded-md shadow-sm">
                <input v-model="form.customer_reference" class="form-input flex-1 block w-full rounded transition duration-150 ease-in-out sm:text-sm sm:leading-5" />
              </div>
            </div>
          </div>


          <div class="mt-6" v-if="selectedAccount">
            <fieldset>
              <legend class="block text-sm font-medium leading-5 text-gray-700">Select Notification Contact</legend>
              <div class="mt-1 rounded-md shadow-sm bg-white">
                <div>
                  <select v-model="notificationContact" aria-label="Country" class="form-select relative block w-full rounded-none rounded-t-md bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
                    <option :value="null">Other</option>
                    <option v-for="user in users" :value="user">{{ `${user.first_name} ${user.last_name}` }}</option>
                  </select>
                </div>
                <div class="-mt-px">
                  <input type="number" :disabled="notificationContact !== null" v-model.number="form.notification_contact_number" class="form-input relative block w-full rounded-none rounded-b-md bg-transparent focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5" placeholder="mobile number" />
                </div>
              </div>
            </fieldset>
          </div>

          <div v-if="form.account_id" class="mt-4 bg-white shadow rounded-lg text-gray-500">
            <div class="border-b border-gray-200 px-6 py-3 sm:px-6 text-xs uppercase leading-4 font-medium">
              Delivery Address
            </div>

            <div class="border-t border-gray-100" />

            <div class="px-4 py-5 sm:p-6">

              <fieldset class="mt-6">
                <legend class="text-base font-medium text-gray-900">
                  Choose collection option
                </legend>
                <div class="mt-4">
                  <div class="flex items-center">
                    <input type="radio" v-model="collection_option" value="self_collect" class="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" />
                    <label class="ml-3">
                      <span class="block text-sm leading-5 font-medium text-gray-700">Self collect from BHM warehouse locations</span>
                    </label>
                  </div>

                  <div class="block mb-3" v-if="form.customer_to_collect">
                    <div class="block pl-7">
                      <label class="block text-xs font-medium leading-5 text-gray-500">
                        Handover contact mobile
                      </label>
                      <div class="mt-1 flex">
                        <text-input v-model="form.contact_number" class="number-no-spin" type="number" :errors="$page.props.errors.contact_number" />
                      </div>
                    </div>
                  </div>

                  <div class="mt-4 flex items-center">
                    <input type="radio" v-model="collection_option" value='delivery' class="form-radio h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" />
                    <label  class="ml-3">
                      <span class="block text-sm leading-5 font-medium text-gray-700">Deliver to
                        <span v-if="selectedAccount.delivery_location === 'vessel_location'"> Boat</span>
                        <span v-else> address</span>
                      </span>
                    </label>
                  </div>

                </div>
              </fieldset>


                <div v-if="collection_option == 'delivery'" class="mt-8">

                  <!-- Delivery to boat -->
                  <label  class="ml-3" v-if="selectedAccount.delivery_location === 'vessel_location'">
                    <span class="block text-sm leading-5 font-medium text-gray-700">Choose boat</span>
                    <v-select v-model="form.boat" label="name" :options="boatList" aria-required="true" required>

                      <template v-slot:option="option">
                        {{ option.name }} <span v-if="option.alt_name" class="text-gray-400 ml-2 text-sm">{{ option.alt_name }}</span>
                      </template>

                      <template v-slot:no-options="{ search, searching }">
                        <template v-if="searching">
                          No matching boat with name <em>{{ search }}</em>.
                        </template>
                        <em style="opacity: 0.5;" v-else>Select or type to search...</em>
                      </template>

                      <template #search="{attributes, events}">
                        <input
                          class="vs__search"
                          :required="!form.boat"
                          v-bind="attributes"
                          v-on="events"
                        />
                      </template>
                  </v-select>
                  </label>

                <!--     Delivery to address    -->
                  <label v-else class="text-sm">Delivery to customer address</label>

                </div>


              <div class="block mb-3 mt-3">
                <div class="block">
                  <label class="block text-sm font-medium leading-5 text-gray-700">
                    Remarks
                  </label>
                  <div class="mt-1 flex rounded-md shadow-sm">
                    <textarea v-model="form.remarks" :class="{'error': $page.props.errors.remarks}" class="form-input flex-1 block w-full rounded transition duration-150 ease-in-out sm:text-sm sm:leading-5" />
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div class="flex mt-3 flex-col">
            <div class="-my-2 py-2 overlfow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
              <div
                class="align-middle inline-block min-w-full shadow overflow-hidden rounded-lg border-b border-gray-200"
              >
                <table class="table-auto min-w-full">
                  <thead>
                  <tr>
                    <th
                      class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Code

                    </th>
                    <th
                      class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell"
                    >
                      Pack Size
                    </th>

                    <th
                      class="w-auto px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase"
                    >
                      Qty
                    </th>
                    <th
                      class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                    />
                  </tr>
                  </thead>
                  <template v-for="(item, index) in form.items">
                    <OrderTableItem :index="index" :item="item"/>
                  </template>
                  <template v-if="form.items.length">
                    <tbody>
                      <tr class="bg-gray-100">
                        <th colspan="3" class="text-right">Total</th>
                        <th class="text-right px-3 py-2">MVR {{ totalSales }}</th>
                      </tr>
                    </tbody>
                  </template>
                  <tbody v-if="form.items.length === 0" class="bg-white">
                  <tr>
                    <td class="border-t px-6 py-4 text-xs" colspan="6">No item added.</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="mt-5 flex justify-end">
            <loading-button :loading="sending" class="btn-primary" type="submit">Place Order</loading-button>
          </div>
        </form>
      </div>
    </div>

<!--    &lt;!&ndash; address add modal &ndash;&gt;-->
<!--    <modal v-if="!form.customer_to_collect" :show="openAddressModal" @closeModal="openAddressModal = false">-->
<!--      <address-form :url="route('ajax.customer-address.store', form.account_id)" :config="addressConfig" :account-id="form.account_id" :location="location"  @input="appendAddress" @close-modal="openAddressModal = false" />-->
<!--    </modal>-->

    <!-- address removal modal -->
    <remove-address v-model="removingAddress"
                    :open="openAddressRemovalConfirmation"
                    :url="removingAddress.id ? route('ajax.customer-address.delete', removingAddress.id) : null"
                    @close-modal="openAddressRemovalConfirmation = false"
                    @removed="removeAddress"
    />
  </div>
</template>

<script>
  import Layout from '@/Shared/Admin/Layout'
  import ProductCatalog from '@/Pages/Admin/CustomerOrders/ProductCatalog'
  import Icon from '@/Shared/Icon'
  import LoadingButton from '@/Shared/LoadingButton'
  import Modal from '@/Shared/Modal'
  import AddressForm from '@/Pages/Customers/MyOrders/AddressForm'
  import RemoveAddress from '@/Pages/Customers/MyOrders/RemoveAddress'
  import TextInput from '@/Shared/TextInput'
  import vSelect from 'vue-select';
  import _ from 'lodash';
  import store from '@/Shared/store'
  import OrderTableItem from '@/Pages/Customers/MyOrders/OrderTableItem'

  store.setCustomerMode()

  export default {
    layout: Layout,
    name: 'Create',
    components: {
      OrderTableItem,
      TextInput,
      RemoveAddress,
      AddressForm,
      Modal, LoadingButton, Icon, ProductCatalog, vSelect},

    props: {
      deliveryLocations: Array,
      enableProductUnitSelection: Boolean,
      addressConfig: {
        type: Object,
      },
      contactNumber: String,
      price_groups: Array,
    },

    data() {
      return {
        mobile: {
          showOrderForm: false,
        },
        delivery_location: null,
        searchText: null,
        shopping_location: null,
        collection_option: 'self_collect',
        openAddressModal: false,
        openAddressRemovalConfirmation: false,
        sending: false,
        addresses: [],
        selectedAccount: null,
        selectedPriceGroup: null,
        customerAccounts: [],
        users: [],
        notificationContact: {},
        form: store.form,
        removingAddress: {},
        boatList: []
      }
    },

    watch: {
      'shopping_location': function(val) {
        localStorage.setItem('my.defaultShoppingLocation', val)
      },

      'collection_option': function(val) {
        if(val == 'self_collect') {
          this.form.customer_to_collect = true;
        } else {
          this.form.customer_to_collect = false;
        }
      },

      'selectedAccount': function(val) {
        if (val) {

          this.form.account_id = val.id

          if(val.price_group) {
            this.selectedPriceGroup = val.price_group
          }

          // this.$http.get(this.route('lists.delivery-address', val.id)).then(response => {
          //   this.addresses = response.data
          // })

          this.$http.get(this.route('ajax.customers.users.list', {customerAccount: val.id})).then(response => {
            if(response.data.length) {
              this.users = response.data;
              this.notificationContact = response.data[0]
              this.form.contact_number = this.notificationContact.mobile
            } else {
              this.users = [];
              this.notificationContact = {}
            }
          })
        }
      },

      'notificationContact': function (val) {
        if (val) {
          this.form.notification_contact_number = val.mobile
        }
      },

      'form.address': function (val) {
        if (val) {
          let address = this.addresses.find(itm => itm.id === val)
          if(address.remarks) {
            this.form.remarks = address.remarks
          }
        }
      },
    },

    mounted() {
      store.resetForm();
      if (this.customerAccounts.length) {
        this.form.account_id = this.customerAccounts[0].id
      }

      this.shopping_location = localStorage.getItem('my.defaultShoppingLocation') || this.addressConfig.shoppingDefaultLocation

      this.form.contact_number = this.contactNumber

      this.$http.get(this.route('ajax.boat-list')).then(response => {
        this.boatList = response.data
      })
    },

    methods: {

      clearItems() {
        this.form.items = [];
      },

      confirmAddressRemoval(address) {
        this.openAddressRemovalConfirmation = true
        this.removingAddress = address
      },

      appendAddress(address) {
        this.addresses.push(address)
        this.openAddressModal = false

        this.form.address = address.id
      },

      removeAddress(address) {
        let index =  this.addresses.indexOf(address)
        this.addresses.splice(index, 1)
        this.removingAddress = {}
        this.openAddressRemovalConfirmation = false
      },

      submit() {
        this.sending = true

        // var data = new FormData();
        // data.append('customer_account_id', this.form.account.id)
        let data = {}
        // data.items = this.form.items
        data.items = this.form.items.map(item => {
          return {
            item_code : item.product.item_code,
            wcode : item.product.wcode,
            qty: item.qty,
            unit: item.unit
          }
        })
        data.price_group = this.selectedPriceGroup
        data.account_id = this.form.account_id
        data.address_id = this.form.address
        data.customer_to_collect = this.form.customer_to_collect === true
        data.remarks = this.form.remarks
        data.customer_reference = this.form.customer_reference
        data.contact_number = this.form.contact_number
        data.notification_contact_number = this.form.notification_contact_number
        data.collection_option = this.collection_option

        if(this.collection_option === 'delivery' && this.selectedAccount.delivery_location === 'vessel_location') {
          data.boat = this.form.boat
          data.boat_location = this.form.boat_location
          if(data.boat.id === 'other') {
            data.other_boat_name = this.form.other_boat_name
          }
        }

        if(this.collection_option === 'delivery')
        {
          data.delivery_location = this.selectedAccount.delivery_location;
        }

        this.$inertia.post(this.route('customer.orders.store'), data,{
          onFinish : () => this.sending = false
        })
      },

      searchCustomerAccounts: _.debounce(function (search, loading) {
        this.$http.get(this.route('ajax.customers.list')+`?search=${search}`)
        .then(response => {
          loading(false)
          this.customerAccounts = response.data.data
        })
      }, 200)
    },

    computed: {

      totalSales() {
        let total = 0;

        if(this.form.items.length > 0) {
          this.form.items.forEach((item) => {
              let unit = item.unit;
              let price = item.product.units[unit].price
              total = total + (item.qty * price)
          })
        }

        return total
      }

      // customerAccounts() {
      //   return this.accounts.map(itm => {
      //     return {
      //       label: `${itm.name} (${itm.tin_number})`,
      //       value: itm.id
      //     }
      //   })
      // }
    }
  }
</script>

